import { useEffect } from "react";
import { Link } from "react-router-dom";
import Nav from "../components/Nav";
import Footer from "../components/Footer";
import GrainOverlay from "../components/GrainOverlay";

const Section = ({ title, children, testId }) => (
  <div className="mt-14 first:mt-0" data-testid={testId}>
    <h2 className="h-display text-2xl md:text-3xl mb-4 text-ink">{title}</h2>
    <div className="space-y-4 text-base md:text-lg leading-relaxed text-ink/80">
      {children}
    </div>
  </div>
);

export default function Terms() {
  useEffect(() => {
    document.title = "Terms of Service | GoneSoon";
    window.scrollTo(0, 0);
  }, []);

  return (
    <div data-testid="terms-page" className="relative">
      <GrainOverlay />
      <div className="bg-cream">
        <Nav variant="cream" />
      </div>

      <section className="bg-cream text-ink">
        <div className="max-w-[900px] mx-auto px-6 md:px-10 pt-16 md:pt-24 pb-10">
          <div className="pill text-ink/70 border-ink/20 mb-8">
            <span className="pill-dot" />
            Legal
          </div>
          <h1 data-testid="terms-headline" className="h-display text-[2.5rem] md:text-[4rem] leading-[0.95] max-w-[16ch]">
            Terms of Service
          </h1>
          <p className="mt-6 text-ink/60 text-sm uppercase tracking-[0.18em]">
            Effective August 3, 2026
          </p>
        </div>

        <div className="max-w-[900px] mx-auto px-6 md:px-10 pb-24 md:pb-32">
          <Section title="Agreement to terms" testId="terms-agreement">
            <p>
              These Terms of Service govern your use of GoneSoon's website, mobile apps, and
              WhatsApp Business number (together, the "Service"). By creating an account, claiming
              a drop, or messaging our WhatsApp number, you agree to these terms.
            </p>
          </Section>

          <Section title="What GoneSoon is" testId="terms-what-is">
            <p>
              GoneSoon is a membership-based platform offering access to limited-time, limited-quantity
              offers ("drops") from selected local merchants, currently launching in Trivandrum.
              Membership is required to claim drops.
            </p>
          </Section>

          <Section title="Eligibility" testId="terms-eligibility">
            <p>
              You must be at least 18 years old and able to form a binding contract to use the
              Service. By using GoneSoon, you confirm you meet this requirement.
            </p>
          </Section>

          <Section title="Your account and WhatsApp communications" testId="terms-account">
            <p>
              You sign in with your mobile number and a one-time code we send you over WhatsApp.
              By providing your number, you consent to receive transactional messages from us over
              WhatsApp — verification codes, onboarding, and updates related to your account or
              redemptions. These are not marketing messages.
            </p>
            <p>
              If you message our WhatsApp Business number as a prospective merchant, you agree
              that the information you share (business category, location, and related details)
              may be used to evaluate and onboard your business, and may be passed to a member of
              our team to follow up with you directly.
            </p>
          </Section>

          <Section title="Membership and subscriptions" testId="terms-membership">
            <p>
              Membership is subscription-based and auto-renews monthly unless cancelled. Where
              offered, a free trial converts automatically to a paid subscription at the end of
              the trial period unless cancelled before it ends. You can cancel anytime from your
              Profile in the app or through your app store account settings.
            </p>
          </Section>

          <Section title="Drops, claims, and redemption" testId="terms-drops">
            <p>
              Claiming a drop reserves it for you; it does not constitute payment. Payment for
              individual drops, where applicable, is made directly with the merchant at the time
              of redemption — GoneSoon does not process merchant payments. Redemption passes (QR
              codes) are one-time-use, non-transferable, and valid only within the stated drop
              window.
            </p>
            <p>
              We may limit or restrict accounts that repeatedly claim drops without redeeming
              them, to protect availability for other members and for our merchant partners.
            </p>
          </Section>

          <Section title="Merchant partners" testId="terms-merchants">
            <p>
              Merchants listed on GoneSoon are independent businesses responsible for the goods
              and services they offer. GoneSoon is not a party to the transaction between you and
              a merchant beyond facilitating the drop and its redemption.
            </p>
          </Section>

          <Section title="Prohibited conduct" testId="terms-prohibited">
            <p>
              You agree not to misuse the Service — including attempting to forge or duplicate
              redemption codes, circumvent membership requirements, or use the Service in a way
              that could harm GoneSoon, our merchants, or other members.
            </p>
          </Section>

          <Section title="Disclaimers and limitation of liability" testId="terms-disclaimers">
            <p>
              The Service is provided "as is." To the maximum extent permitted by law, GoneSoon is
              not liable for indirect, incidental, or consequential damages arising from your use
              of the Service or your dealings with merchant partners.
            </p>
          </Section>

          <Section title="Termination" testId="terms-termination">
            <p>
              We may suspend or terminate your account if you violate these terms. You may stop
              using the Service and cancel your membership at any time.
            </p>
          </Section>

          <Section title="Governing law" testId="terms-governing-law">
            <p>
              These terms are governed by the laws of India, and disputes are subject to the
              exclusive jurisdiction of the courts of Kerala, India.
            </p>
          </Section>

          <Section title="Changes to these terms" testId="terms-changes">
            <p>
              We may update these terms from time to time. If we make material changes, we'll
              update the effective date above and, where appropriate, notify you directly.
            </p>
          </Section>

          <Section title="Contact us" testId="terms-contact">
            <p>
              Questions about these terms? Email us at{" "}
              <a href="mailto:hello@gonesoon.in" className="ed-link text-ink">hello@gonesoon.in</a>.
            </p>
          </Section>

          <div className="mt-16 pt-10 border-t border-ink/15">
            <Link to="/privacy" data-testid="terms-link-privacy" className="ed-link text-ink">
              Read our Privacy Policy →
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
